import React, { useContext, useState } from 'react';
import { filterCustomProperties } from 'shared/utils/properties';
import { Form, Row } from 'react-bootstrap';
import { Scrollbars } from 'react-custom-scrollbars';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import { useSelector } from 'shared/node_modules/react-redux';
import ENVIRONMENT_VARIABLES from 'shared/utils/environmentVariables';
import styled from 'styled-components';
import theme from 'shared/assets/style/theme';
import ThemeContext from 'shared/context/ThemeContext';
import { checkIfOtpRequeridedConsents } from 'shared/screens/OneTimePassword/utils';
import {
  registerCardsFormKeys,
  generateReqObject,
  setPropertyId,
  checkResponseData,
  closeModal,
  checkUserProperty,
} from 'shared/screens/RegisterRewardCard/utils';
import { styles } from './styles';
import FormInputs from './components/FormInputs';
import FormFooter from './components/FormFooter';
import Button from '../../components/Button/Button';
import ImageBW from '../../components/ImageBoundsWrapper';
import TermsAndConditions from '../../components/TermsAndConditions';
import useRegisterCardForm from 'shared/screens/RegisterRewardCard/hooks/useRegisterCardForm';
import { usePostRegisterNewCardMutation } from 'shared/state/query/RegisterNewCard';
import { TERMS_TYPE } from 'shared/constants/types';

export default function RegisterRewardCard() {
  const themeContext = useContext(ThemeContext);
  const [postRegisterNewCard, { isLoading, data, error }] = usePostRegisterNewCardMutation();
  const { userProperty, propertySelector } = useSelector(state => state.properties.data);
  const termsAndConditionsUrl = useSelector(state => state.assetsUrls?.termsAndConditions);
  const [selectedPropertyTermsURLs, changeTCLink] = useState();
  const [isOpenTerms, changeOpenTerm] = useState({
    show: false,
    docType: TERMS_TYPE.TERMS,
  });
  const [activeOTPCheckbox, changeOTPCheckBoxState] = useState(false);
  const [selectedProperty, changeSelectedProperty] = useState(checkUserProperty(propertySelector, userProperty) || {});
  const showProperty = ENVIRONMENT_VARIABLES.SHOW_PROPERTY_SELECTOR === 'true';

  const submitFormHandler = formInputs => {
    const request = generateReqObject(formInputs);
    const { token, ...requestObject } = request;
    if (!showProperty) {
      requestObject[registerCardsFormKeys.PROPERTY] = userProperty;
    }
    postRegisterNewCard(requestObject);
  };

  const isWeb = true;

  const { values, handleSubmit, handleBlur, handleChange, setFieldValue, isValid, propertyRequiredFields } =
    useRegisterCardForm({
      isWeb,
      showProperty,
      submitFormHandler,
      selectedProperty,
      userProperty,
    });

  const findPropertyLinks = propertyId => {
    const selectedPropertyObj = propertySelector.find(property => property.propertyId === Number(propertyId));
    if (!selectedPropertyObj) return null;
    changeSelectedProperty(selectedPropertyObj);
    return {
      termsAndConditions: selectedPropertyObj?.loyaltyTermsAndConditions,
      privacyPolicy: selectedPropertyObj?.loyaltyPrivacyPolicy,
    };
  };

  const handleOPTCheckbox = () => {
    changeOTPCheckBoxState(state => !state);
  };

  const changeProperty = propertyId => {
    setFieldValue(registerCardsFormKeys.PROPERTY, propertyId);
    const newLinks = findPropertyLinks(propertyId);
    changeTCLink(newLinks);
  };

  const handleToggleTerms = docType =>
    changeOpenTerm(openTerm => {
      return {
        show: !openTerm.show,
        docType: docType || TERMS_TYPE.TERMS,
      };
    });

  const customPropertyOption =
    userProperty === 1
      ? null
      : propertySelector.filter(filterCustomProperties(userProperty)).map(property => ({
          value: property.propertyId,
          label: property.propertyName,
        }));

  const showOtpCheckbox =
    selectedProperty?.requiresConsents ||
    checkIfOtpRequeridedConsents(setPropertyId(userProperty, values[registerCardsFormKeys.PROPERTY]), propertySelector);

  const responseCheck = checkResponseData(data, error);

  const isReadyToSend = isValid && (!showOtpCheckbox || (showOtpCheckbox && activeOTPCheckbox));

  const { isPropertySelected } = values;

  return (
    <>
      <Header>
        <ImageBW
          source={require(asset`Full_Modal_Header.png`)}
          ibw={require(asset`Full_Modal_Header.ibw`)}
          style={themeContext.RegisterRewardCard.HeaderImage}
        >
          {isOpenTerms.show && (
            <BackArrowContainer onClick={handleToggleTerms}>
              <ImageBW
                source={require(asset`Back_Arrow@3x.png`)}
                ibw={require(asset`Back_Arrow.ibw`)}
                style={styles.backArrow}
                alt="Close"
                widthScale={0.5}
              />
            </BackArrowContainer>
          )}
          <div style={themeContext.RegisterRewardCard.HeaderTitle}>
            <span>{isOpenTerms.show ? getText(TEXT_KEY.TERMS_AND_CONDITIONS_TITLE) : getText(TEXT_KEY.REGISTER_REWARD_CARD_TITLE)}</span>
          </div>
        </ImageBW>
      </Header>
      {!responseCheck?.success && (
        <p style={themeContext.RegisterRewardCard.FormDescription}>{getText(TEXT_KEY.REGISTER_REWARD_CARD_DESCRIPTION)}</p>
      )}
      {!isOpenTerms.show ? (
        <ContentWrapper success={responseCheck?.success ? 1 : 0}>
          {!responseCheck?.success ? (
            <Scrollbars
              style={styles.scrollBar}
              renderThumbVertical={props => <div {...props} className="thumb-vertical" style={{ width: '0.5rem' }} />}
              renderTrackVertical={props => <div {...props} className="track-vertical" style={{ opacity: 1 }} />}
              renderThumbHorizontal={props => (
                <div {...props} className="thumb-horizontal" style={{ display: 'none' }} />
              )}
              renderTrackHorizontal={props => (
                <div {...props} className="track-horizontal" style={{ display: 'none' }} />
              )}
            >
              <ScrollContent>
                <Form className="form-container payment-info" onSubmit={handleSubmit}>
                  <Form.Group className="form-input-group">
                    <FormInputs
                      propertyRequiredFields={propertyRequiredFields}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      changeProperty={changeProperty}
                      showProperty={showProperty}
                      values={values}
                      customPropertyOption={customPropertyOption}
                    />
                    {(!showProperty || isPropertySelected || customPropertyOption) && (
                      <FormFooter
                        termsAndConditionsURL={termsAndConditionsUrl}
                        handleOpenTerms={handleToggleTerms}
                        showOTPConsents={showOtpCheckbox}
                        activeOTPCheckbox={activeOTPCheckbox}
                        handleOPTCheckbox={handleOPTCheckbox}
                        selectedPropertyObj={selectedProperty}
                      />
                    )}
                    {!isReadyToSend && (
                      <FormMandatoryMessage>{getText(TEXT_KEY.REGISTER_REWARD_CARD_ERROR_MESSAGE)}</FormMandatoryMessage>
                    )}
                    {responseCheck && (
                      <MessageHolder>
                        <div className="message">{responseCheck.message}</div>
                      </MessageHolder>
                    )}
                    <RowHolderCenterItem>
                      <Button
                        imageSource={
                          isReadyToSend && !isLoading
                            ? require(asset`RoundedRect_Pink.png`)
                            : require(asset`RoundedRect_Grey.png`)
                        }
                        label={getText(TEXT_KEY.REGISTER_REWARD_CARD_BUTTON)}
                        onClick={handleSubmit}
                        isDebounce={true}
                        disabled={isLoading || !isReadyToSend}
                      />
                    </RowHolderCenterItem>
                  </Form.Group>
                </Form>
              </ScrollContent>
            </Scrollbars>
          ) : (
            <RowHolderCenterItem>
              <MessageHolder>
                <div className="message">{responseCheck?.message}</div>
              </MessageHolder>
              <Button
                imageSource={require(asset`RoundedRect_Pink.png`)}
                label={getText(TEXT_KEY.DONE)}
                onClick={closeModal}
                isDebounce={false}
              />
            </RowHolderCenterItem>
          )}
        </ContentWrapper>
      ) : (
        <TermsWrapper>
          <TermsAndConditions docType="TERMS" selectedPropertyTerms={selectedPropertyTermsURLs} />
        </TermsWrapper>
      )}
    </>
  );
}

const Header = styled.div`
  width: 100%;
  height: 3.7rem;
`;

const TermsWrapper = styled.div`
  margin: 20px;
`;

const BackArrowContainer = styled.div`
  cursor: pointer;
  z-index: 100;
  position: absolute;
  height: 2.3rem;
  width: 1.2rem;
  top: 1rem;
  left: 2rem;
`;

const ContentWrapper = styled.div`
  margin: 5px;
  height: ${({ success }) => (success ? 20 : 65)}vh;
`;

const ScrollContent = styled.div`
  margin: 20px;
`;

const RowHolder = styled(Row)`
  margin-right: 15px;
  margin-left: 15px;
  margin-top: 5px;
`;

const RowHolderCenterItem = styled(RowHolder)`
  justify-content: center;
  margin-top: 2.5em;
  margin-bottom: 0.5em;
`;

const MessageHolder = styled(RowHolder)`
  justify-content: center;
  color: ${theme.palette.common[1]};
  text-align: center;
  margin-bottom: 35px;
`;

const FormMandatoryMessage = styled.p`
  text-align: center;
  color: ${theme.palette.error[1]};
`;

